/**
 * Pure date / time / blackout formatters shared across the scheduling overview
 * calendar. Extracted out of SchedulingOverviewCalendar.js to keep the main
 * file smaller and to avoid pulling these helpers into other modules that only
 * need formatting.
 *
 * Every helper takes an explicit `tz` argument — no module-level state.
 */

/**
 * @param {Date} date
 * @param {string|null|undefined} tz IANA timezone (e.g. "America/New_York"); when
 *   omitted, formatting falls back to the host timezone.
 */
const isIanaTz = (tz) => typeof tz === "string" && tz.includes("/");

export function formatAmPm(date, tz) {
  const opts = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  if (isIanaTz(tz)) opts.timeZone = tz;
  return date.toLocaleTimeString("en-US", opts);
}

/** e.g. 7 → "7:00 AM" */
export function formatHourLabel12(hour24, tz) {
  const d = new Date();
  d.setHours(hour24, 0, 0, 0);
  const opts = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  if (isIanaTz(tz)) opts.timeZone = tz;
  return d.toLocaleTimeString("en-US", opts);
}

export function formatBlackoutSummaryHeadingDate(d) {
  const x = new Date(d);
  let month = x.toLocaleDateString("en-US", { month: "short" });
  if (month === "Sep") month = "Sept";
  const dayNum = x.getDate();
  const year = x.getFullYear();
  const wd = x.toLocaleDateString("en-US", { weekday: "short" });
  return `${month} ${dayNum}, ${year} (${wd})`;
}

export function formatBlackoutSummaryTimeRange(row, tz) {
  const o = row.blackoutOverride;
  if (!o) return "Full day";
  if (o.override_type === "blackout") {
    if (
      (o.start_time == null || o.start_time === "") &&
      (o.end_time == null || o.end_time === "")
    ) {
      return "Full day";
    }
    return `${formatAmPm(row.start, tz)} – ${formatAmPm(row.end, tz)}`;
  }
  if (o.time_mode === "full_day") return "Full day";
  return `${formatAmPm(row.start, tz)} – ${formatAmPm(row.end, tz)}`;
}

/** Time line for month popover (hyphen separators, matches design). */
export function formatMonthBlackoutPopoverTimeRange(row, tz) {
  const s = formatBlackoutSummaryTimeRange(row, tz);
  return s.replace(/\s*[–—]\s*/g, " - ");
}

export function formatMonthBlackoutTooltip(row, tz) {
  return `${formatBlackoutSummaryHeadingDate(row.start)} — ${formatBlackoutSummaryTimeRange(row, tz)}`;
}

export function formatFullDayBlackoutBadgeTitle(row, tz) {
  if (!row) return undefined;
  if (row.fullDayTooltip) return row.fullDayTooltip;
  return formatMonthBlackoutTooltip(row, tz);
}
